/**
 * The `/app` landing page and the user-agent sniff that decides where it
 * sends a phone. Shares the store URLs with the in-page app banner so the
 * proxy redirect, the banner and the landing-page buttons can never disagree.
 */
import { APP_STORE_URL, GOOGLE_PLAY_URL, type MobileAppBannerPlatform } from './mobile-app-banner';

export { APP_STORE_URL, GOOGLE_PLAY_URL };
export type MobilePlatform = MobileAppBannerPlatform;

/** Public path of the app landing page. */
export const APP_LANDING_PATH = '/app';

/** `?view=web` on `/app` shows the landing page even on a phone (QA, support links). */
export const APP_LANDING_WEB_VIEW_PARAM = 'view';
export const APP_LANDING_WEB_VIEW_VALUE = 'web';

export const STORE_URL_BY_PLATFORM: Record<MobilePlatform, string> = {
	ios: APP_STORE_URL,
	android: GOOGLE_PLAY_URL,
};

/** Product token the native app appends to its WebView user agents (apps/mobile/src/lib/web-view-user-agent.ts). */
const WORKMAN_APP_UA_PATTERN = /\bWorkmanApp\//;

/**
 * Best-effort platform sniff from a user-agent string alone. Only phones and
 * tablets that identify themselves count; desktop browsers (including iPadOS
 * in its "Macintosh" desktop-site disguise) and the Workman app's own WebViews
 * return null so they get the page.
 */
export function detectMobilePlatform(userAgent: string | null | undefined): MobilePlatform | null {
	if (!userAgent || WORKMAN_APP_UA_PATTERN.test(userAgent)) return null;
	if (/android/i.test(userAgent)) return 'android';
	if (/iphone|ipad|ipod/i.test(userAgent)) return 'ios';
	return null;
}

/** Whether a request for `/app` should be sent to a store instead of rendered. */
export function getAppStoreRedirectUrl(
	pathname: string,
	searchParams: URLSearchParams,
	userAgent: string | null | undefined,
): string | null {
	if (pathname !== APP_LANDING_PATH && pathname !== `${APP_LANDING_PATH}/`) return null;
	if (searchParams.get(APP_LANDING_WEB_VIEW_PARAM) === APP_LANDING_WEB_VIEW_VALUE) return null;

	const platform = detectMobilePlatform(userAgent);
	return platform ? STORE_URL_BY_PLATFORM[platform] : null;
}
